import { motion } from "framer-motion";

// Elegant manuscript-style placeholders with ornamental SVG borders
const ORNAMENTS = {
    architecture: (
        <svg viewBox="0 0 200 280" className="absolute inset-0 w-full h-full opacity-25" preserveAspectRatio="xMidYMid slice">
            <defs>
                <pattern id="dots-arch" patternUnits="userSpaceOnUse" width="8" height="8">
                    <circle cx="4" cy="4" r="0.5" fill="currentColor" opacity="0.4" />
                </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots-arch)" />
            {/* Arched window */}
            <path d="M 60 240 L 60 100 Q 60 50 100 50 Q 140 50 140 100 L 140 240 Z" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
            <line x1="100" y1="50" x2="100" y2="240" stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
            <line x1="60" y1="160" x2="140" y2="160" stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
        </svg>
    ),
    flame: (
        <svg viewBox="0 0 200 280" className="absolute inset-0 w-full h-full opacity-30" preserveAspectRatio="xMidYMid slice">
            <radialGradient id="flame" cx="50%" cy="70%" r="40%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.6" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </radialGradient>
            <ellipse cx="100" cy="180" rx="50" ry="80" fill="url(#flame)" />
            <path d="M 100 250 Q 75 180 90 130 Q 105 100 100 70 Q 115 100 110 130 Q 125 180 100 250 Z" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.5" />
        </svg>
    ),
    interior: (
        <svg viewBox="0 0 200 280" className="absolute inset-0 w-full h-full opacity-20" preserveAspectRatio="xMidYMid slice">
            {/* Beams / artesonado */}
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <line key={i} x1={i * 25} y1="0" x2={i * 25 + 80} y2="280" stroke="currentColor" strokeWidth="0.4" opacity="0.4" />
            ))}
            {[40, 90, 140, 190, 240].map((y) => (
                <line key={y} x1="0" y1={y} x2="200" y2={y} stroke="currentColor" strokeWidth="0.4" opacity="0.5" />
            ))}
        </svg>
    ),
    leaves: (
        <svg viewBox="0 0 200 280" className="absolute inset-0 w-full h-full opacity-25" preserveAspectRatio="xMidYMid slice">
            <g fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.5">
                <path d="M 40 50 Q 60 60 70 80 Q 80 100 75 130" />
                <path d="M 160 70 Q 145 90 150 120 Q 155 150 135 170" />
                <path d="M 50 200 Q 80 195 100 215 Q 120 235 150 230" />
                <ellipse cx="65" cy="75" rx="5" ry="12" transform="rotate(45 65 75)" />
                <ellipse cx="155" cy="100" rx="5" ry="12" transform="rotate(-30 155 100)" />
                <ellipse cx="120" cy="225" rx="5" ry="12" transform="rotate(20 120 225)" />
            </g>
        </svg>
    ),
    wave: (
        <svg viewBox="0 0 200 280" className="absolute inset-0 w-full h-full opacity-25" preserveAspectRatio="xMidYMid slice">
            <g fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.6">
                {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                    <path key={i} d={`M 0 ${40 + i * 35} Q 50 ${30 + i * 35} 100 ${40 + i * 35} T 200 ${40 + i * 35}`} />
                ))}
            </g>
        </svg>
    ),
    abstract: (
        <svg viewBox="0 0 200 280" className="absolute inset-0 w-full h-full opacity-30" preserveAspectRatio="xMidYMid slice">
            <g fill="none" stroke="currentColor" strokeWidth="0.7">
                <circle cx="100" cy="140" r="60" opacity="0.4" />
                <circle cx="100" cy="140" r="40" opacity="0.5" />
                <circle cx="100" cy="140" r="20" opacity="0.7" />
                <line x1="0" y1="140" x2="200" y2="140" opacity="0.2" />
                <line x1="100" y1="0" x2="100" y2="280" opacity="0.2" />
            </g>
        </svg>
    ),
};

export default function Placeholder({
    label,
    ornament = "abstract",
    ratio = "aspect-[3/4]",
    className = "",
    number,
}) {
    const Orn = ORNAMENTS[ornament] || ORNAMENTS.abstract;
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className={`placeholder-art text-gold ${ratio} w-full flex items-center justify-center ${className}`}
        >
            {Orn}
            <div className="relative z-10 text-center px-4">
                {number && (
                    <p className="font-editorial italic text-sm text-gold/60 tracking-[0.3em] mb-2">
                        {number}
                    </p>
                )}
                <p className="font-display italic text-gold/70 text-lg tracking-wider">
                    {label}
                </p>
            </div>
        </motion.div>
    );
}
